import { useState } from "react";
import { C, MUSCLE_GROUPS, CARDIO_TYPES, STEP_BASED, EXERCISE_LIBRARY } from "../constants";
import { toBaseWeight, toBaseDist, weightLabel } from "../utils/units";
import { Modal, Field, Btn, Badge, iStyle, lblSt } from "./ui";

let _eid = 0;
const makeEx = () => ({
  _id: Date.now() + (++_eid),
  type: "strength",
  name: "",
  muscleGroup: "Chest",
  sets: [{ reps: "", weight: "" }],
  cardioType: "Running",
  duration: "",
  distance: "",
  calories: "",
  showSug: false,
});

export const WorkoutForm = ({ onAdd, onClose, settings, customExercises = [] }) => {
  const fullLibrary = [...EXERCISE_LIBRARY, ...customExercises];
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [exs,  setExs]  = useState([makeEx()]);

  const upd    = (id, patch)        => setExs(prev => prev.map(e => e._id === id ? { ...e, ...patch } : e));
  const addEx  = ()                 => setExs(prev => [...prev, makeEx()]);
  const rmEx   = id                 => setExs(prev => prev.filter(e => e._id !== id));
  const addSet = id                 => setExs(prev => prev.map(e => e._id === id ? { ...e, sets: [...e.sets, { reps: "", weight: "" }] } : e));
  const rmSet  = (id, si)           => setExs(prev => prev.map(e => e._id === id ? { ...e, sets: e.sets.filter((_, i) => i !== si) } : e));
  const updSet = (id, si, key, val) => setExs(prev => prev.map(e => e._id === id ? { ...e, sets: e.sets.map((s, i) => i === si ? { ...s, [key]: val } : s) } : e));

  const submit = () => {
    if (!date) return;
    const sessionId = `session_${Date.now()}`;
    let added = 0;
    exs.forEach(ex => {
      if (ex.type === "strength") {
        if (!ex.name) return;
        const valid = ex.sets.filter(s => s.reps && s.weight !== "");
        if (!valid.length) return;
        const lower = ex.name.toLowerCase();
        const libMatch = fullLibrary.find(e => e.name.toLowerCase() === lower);
        const exerciseId = libMatch?.id || null;
        valid.forEach((s, si) => {
          onAdd({
            date, type: "strength", name: ex.name, muscleGroup: ex.muscleGroup,
            exerciseId,
            id: Date.now() + Math.random(),
            sets: 1, reps: +s.reps,
            weight: toBaseWeight(s.weight || 0, settings.weightUnit),
            sessionId,
          });
          added++;
        });
      } else {
        const isStep = STEP_BASED.includes(ex.cardioType);
        onAdd({
          date, type: "cardio",
          name: ex.name || ex.cardioType,
          id: Date.now() + Math.random(),
          duration: +ex.duration || 0,
          distance: isStep ? (+ex.distance || 0) : toBaseDist(ex.distance || 0, settings.distUnit),
          calories: +ex.calories || 0,
          cardioType: ex.cardioType,
          sessionId,
        });
        added++;
      }
    });
    if (added > 0) onClose();
  };

  const wl = weightLabel(settings.weightUnit);

  return (
    <Modal onClose={onClose} title="Log Workout" wide>
      <Field label="Date" style={{ marginBottom: 14 }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={iStyle} />
      </Field>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 480, overflowY: "auto", marginBottom: 12 }}>
        {exs.map(ex => {
          const isStep      = STEP_BASED.includes(ex.cardioType);
          const suggestions = ex.name.length > 0
            ? fullLibrary.filter(e => e.name.toLowerCase().includes(ex.name.toLowerCase())).slice(0, 8)
            : [];

          return (
            <div key={ex._id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, flexShrink: 0 }}>
              {/* Header: type toggle + remove */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ display: "flex", gap: 4 }}>
                  {["strength", "cardio"].map(t => (
                    <button key={t} onClick={() => upd(ex._id, { type: t })} style={{
                      padding: "4px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                      fontWeight: 600, fontSize: 12, textTransform: "capitalize",
                      background: ex.type === t ? (t === "strength" ? C.accent : C.blue) : C.card,
                      color: ex.type === t ? "#1e1d1b" : C.muted,
                    }}>{t}</button>
                  ))}
                </div>
                {exs.length > 1 && (
                  <button onClick={() => rmEx(ex._id)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 20, lineHeight: 1, padding: "0 2px" }}>×</button>
                )}
              </div>

              {ex.type === "strength" ? (
                <>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    {/* Exercise name + autocomplete */}
                    <div style={{ flex: 1, position: "relative" }}>
                      <div style={{ ...lblSt, marginBottom: 3 }}>Exercise</div>
                      <input
                        placeholder="Bench Press"
                        value={ex.name}
                        onChange={e => upd(ex._id, { name: e.target.value, showSug: true })}
                        onFocus={() => upd(ex._id, { showSug: true })}
                        onBlur={() => setTimeout(() => upd(ex._id, { showSug: false }), 150)}
                        style={iStyle}
                      />
                      {ex.showSug && suggestions.length > 0 && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, maxHeight: 180, overflowY: "auto", marginTop: 2, boxShadow: "0 6px 20px rgba(0,0,0,0.3)" }}>
                          {suggestions.map(e => (
                            <div key={e.id || e.name} onMouseDown={() => upd(ex._id, { name: e.name, muscleGroup: e.muscleGroup, showSug: false })}
                              style={{ padding: "7px 12px", cursor: "pointer", borderBottom: `1px solid ${C.border}`, color: C.text, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span>{e.name}</span><Badge>{e.muscleGroup}</Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Muscle group */}
                    <div style={{ width: 130 }}>
                      <div style={{ ...lblSt, marginBottom: 3 }}>Muscle</div>
                      <select value={ex.muscleGroup} onChange={e => upd(ex._id, { muscleGroup: e.target.value })} style={iStyle}>
                        {MUSCLE_GROUPS.map(g => <option key={g}>{g}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Sets */}
                  <div style={{ ...lblSt, marginBottom: 6 }}>Sets</div>
                  {ex.sets.map((s, si) => (
                    <div key={si} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 13, color: C.muted, minWidth: 18 }}>{si + 1}.</span>
                      <input type="number" placeholder="Reps" value={s.reps}
                        onChange={e => updSet(ex._id, si, "reps", e.target.value)}
                        style={{ ...iStyle, flex: 1, padding: "6px 8px", fontSize: 13 }} />
                      <input type="number" placeholder={`Weight (${wl})`} value={s.weight}
                        onChange={e => updSet(ex._id, si, "weight", e.target.value)}
                        style={{ ...iStyle, flex: 1, padding: "6px 8px", fontSize: 13 }} />
                      {ex.sets.length > 1 && (
                        <button onClick={() => rmSet(ex._id, si)} style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 16, padding: "0 3px" }}>×</button>
                      )}
                    </div>
                  ))}
                  <Btn variant="outline" onClick={() => addSet(ex._id)} style={{ width: "100%", padding: "6px 0", fontSize: 12, marginTop: 2 }}>+ Add Set</Btn>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...lblSt, marginBottom: 3 }}>Activity</div>
                      <select value={ex.cardioType} onChange={e => upd(ex._id, { cardioType: e.target.value })} style={iStyle}>
                        {CARDIO_TYPES.map(g => <option key={g}>{g}</option>)}
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...lblSt, marginBottom: 3 }}>Label <span style={{ color: C.muted, fontWeight: 400 }}>(optional)</span></div>
                      <input placeholder={ex.cardioType} value={ex.name} onChange={e => upd(ex._id, { name: e.target.value })} style={iStyle} />
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...lblSt, marginBottom: 3 }}>Duration (min)</div>
                      <input type="number" placeholder="30" value={ex.duration} onChange={e => upd(ex._id, { duration: e.target.value })} style={iStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...lblSt, marginBottom: 3 }}>{isStep ? "Steps" : `Distance (${settings.distUnit})`}</div>
                      <input type="number" placeholder={isStep ? "3000" : "3"} value={ex.distance} onChange={e => upd(ex._id, { distance: e.target.value })} style={iStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ ...lblSt, marginBottom: 3 }}>Calories</div>
                      <input type="number" placeholder="300" value={ex.calories} onChange={e => upd(ex._id, { calories: e.target.value })} style={iStyle} />
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <Btn variant="outline" onClick={addEx} style={{ width: "100%", marginBottom: 12 }}>+ Add Exercise</Btn>
      <Btn variant="primary" onClick={submit} style={{ width: "100%", padding: "13px 0", fontSize: 15 }}>
        Log {exs.length > 1 ? "Session" : "Workout"}
      </Btn>
    </Modal>
  );
};
